import { useCallback, useEffect, useRef, useState } from "react";
import {
  ActionButton,
  FileTrigger,
  Flex,
  Text,
  TextField,
  View,
  Content,
} from "@adobe/react-spectrum";
import { useTranslation } from "react-i18next";
import {
  AttachmentIcon,
  CancelIcon,
  MoreIcon,
  NewMessageIcon,
  TemplateIcon,
} from "../../../../../components/protected/Icons/IconsLib";
import {
  DropDownList,
  DropDownListChangeEvent,
} from "@progress/kendo-react-dropdowns";
import { RadioButton } from "@progress/kendo-react-inputs";
import { Loader } from "@progress/kendo-react-indicators";
import { useDependency } from "../../../../../contexts/DependencyProvider";
import ContactsContactMultipleTag from "../../../../../components/protected/layout-components/FilterComponent/ContactsContactMultipleTagComponent/ContactsContactMultipleTag";
import ContactsFilterMultipleTag from "../../../../../components/protected/layout-components/FilterComponent/ContactsFilterMultipleTagComponent/ContactsFilterMultipleTag";
import WindowComponent from "../../../../../components/ui/WindowComponent";
import { invokeNewMessageCreated } from "../../../../../utils/NavigationUtils";
import { useViewInfo } from "../../../../../hooks/UseViewInfo";
import TinyMCEEditor from "../../../../../components/ui/TinyMCEEditor/TinyMCEEditior";
import {
  ContextMenuComponent,
  MenuItemModel,
} from "@syncfusion/ej2-react-navigations";
import { useLocation } from "react-router-dom";
import { SessionInfo } from "../../../../../data/SessionInfo";
import styles from "./new_message_component.module.css";

// Import shared message hooks and utilities
import {
  useNewMessageForm,
  useNewMessageSettings,
  useNewMessageFiles,
  sendMessage,
  saveDraftMessage,
  truncateText,
  type MessageOperationsConfig,
} from "../../../../../hooks/message";

export interface INewMessageDialogProps {
  windowIndex: number;
  selectedItemId: any;
  replyType?: string;
  messageObj?: any;
  emailAttachments?: any;
  isOpen: boolean;
  messageType?: string;
  closeMessageDialog: (reload: boolean) => void;
}

function NewMessageDialog(props: Readonly<INewMessageDialogProps>) {
  const { state } = useLocation();
  const session: SessionInfo = state.session;
  const { isMobile } = useViewInfo();
  const {
    windowIndex,
    selectedItemId,
    isOpen,
    closeMessageDialog,
    replyType,
    messageObj,
    emailAttachments,
    // messageType is passed but not used in this component
  } = props;
  const { store, messageService, mailService } = useDependency();
  const { t } = useTranslation();

  // Use shared hooks
  const {
    formData: newEmailFields,
    formStateRef,
    setFormData: setNewEmailFields,
    createParams,
  } = useNewMessageForm();

  const [fromEmailAddress, setFromEmailAddress] = useState<any>([]);
  const [messageTypeOptions, setMessageTypeOptions] = useState<any>([]);
  const [showLoader, setShowLoader] = useState<boolean>(false);
  const [showCcNotify, setShowCcNotify] = useState<boolean>(false);

  const txtSubjectRef = useRef<any>(null);
  const contextMenuRef = useRef<ContextMenuComponent | null>(null);

  // Use shared settings hook
  const {
    isLoading: settingsLoading,
    mailboxSignature,
    changeMailbox,
    populateReplyTemplate,
  } = useNewMessageSettings({
    session,
    messageObj,
    replyType,
    onSettingsLoaded: (settings, defaultFields) => {
      setFromEmailAddress(settings.mailboxes);
      setMessageTypeOptions(settings.messageTypes);
      setNewEmailFields({
        ...defaultFields,
        from: defaultFields.from,
        fromValue: defaultFields.fromValue,
        messageType: defaultFields.messageType,
        messageTypeValue: defaultFields.messageTypeValue,
        mailBox: settings.mailboxSignature,
        emailContent: settings.mailboxSignature,
      });
    },
    onError: (error) => {
      console.error("Failed to load communication settings", { error });
    },
  });

  // Use shared file management hook
  const {
    fileList,
    handleFilesAsync,
    deleteFile,
    uploadFiles,
    getRootProps,
    getInputProps,
    isDragActive,
  } = useNewMessageFiles({
    contextId: selectedItemId,
    initialAttachments: emailAttachments,
  });

  let menuItems: MenuItemModel[] = [
    {
      iconCss: "bi bi-file-plus-fill",
      text: t("Template", { ns: "layout_components" }),
      id: "template",
    },
    {
      iconCss: "bi bi-gear-fill",
      text: t("Advance", { ns: "layout_components" }),
      id: "advance",
    },
    {
      iconCss: "bi bi-pencil-square",
      text: t("save_draft_and_close", { ns: "layout_components" }),
      id: "draft",
    },
  ];

  // Show loader while settings are loading
  useEffect(() => {
    setShowLoader(settingsLoading);
  }, [settingsLoading]);

  // Handle reply template population
  useEffect(() => {
    if (mailboxSignature && replyType && messageObj) {
      const replyFields = populateReplyTemplate();
      if (replyFields) {
        setNewEmailFields((prev: any) => ({ ...prev, ...replyFields }));
        if (
          (replyFields.cc?.length ?? 0) > 0 ||
          (replyFields.notify?.length ?? 0) > 0
        ) {
          setShowCcNotify(true);
        }
      }
    }
  }, [
    mailboxSignature,
    replyType,
    messageObj,
    populateReplyTemplate,
    setNewEmailFields,
  ]);

  // Message operations config
  const getMessageOpsConfig = useCallback(
    (): MessageOperationsConfig => ({
      mailService,
      messageService,
      serverService: store.Server,
      sessionId: store.SessionId,
      contextId: selectedItemId,
      messageTypes: messageTypeOptions,
      parentMessageId: messageObj?.id,
    }),
    [
      mailService,
      messageService,
      store.Server,
      store.SessionId,
      selectedItemId,
      messageTypeOptions,
      messageObj,
    ],
  );

  // Send email/message using shared utility
  const handleSendEmail = useCallback(async () => {
    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await sendMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        invokeNewMessageCreated(selectedItemId, "message_created");
        closeMessageDialog(true);
      }
    } finally {
      setShowLoader(false);
    }
  }, [
    getMessageOpsConfig,
    uploadFiles,
    closeMessageDialog,
    selectedItemId,
    formStateRef,
  ]);

  // Save draft using shared utility
  const handleSaveDraft = useCallback(async () => {
    try {
      setShowLoader(true);
      const documentIds = await uploadFiles();
      const result = await saveDraftMessage(
        getMessageOpsConfig(),
        formStateRef.current,
        documentIds,
        formStateRef.current.emailContent,
      );
      if (result.success) {
        closeMessageDialog(false);
      }
    } finally {
      setShowLoader(false);
    }
  }, [getMessageOpsConfig, uploadFiles, closeMessageDialog, formStateRef]);

  // Context menu event listener setup
  useEffect(() => {
    const targetElement = document.querySelector(".context-target");
    if (targetElement) {
      targetElement.addEventListener("click", handleContextMenu);
    }
    return () => {
      if (targetElement) {
        targetElement.removeEventListener("click", handleContextMenu);
      }
    };
  }, [newEmailFields]);

  const handleContextMenu = (event: any) => {
    // Prevent the default context menu
    event.preventDefault();
    // Open the custom context menu at the click position
    if (contextMenuRef.current) {
      contextMenuRef.current.open(event.pageY, event.pageX);
    }
  };

  const handleMenuItemClick = useCallback(
    (args: any) => {
      switch (args?.item?.id) {
        case "template":
          console.log("Template clicked");
          break;
        case "advance":
          console.log("Advance clicked");
          break;
        case "draft":
          handleSaveDraft();
          break;
      }
    },
    [handleSaveDraft],
  );

  const handleClose = async (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Escape") {
      closeDialog(false);
    }
  };

  const closeDialog = (flag: boolean) => {
    closeMessageDialog(flag);
  };

  // Mailbox change handler using shared hook
  const handleMailboxChange = async (event: any) => {
    const fromEmailId = fromEmailAddress.find(
      (item: any) => item.name === event.value.name,
    )?.id;
    const newSignature = await changeMailbox(fromEmailId);
    setNewEmailFields((prev: any) => ({
      ...prev,
      from: event.value.name,
      fromValue: { name: event.value.name, id: event.value.id },
      mailBox: newSignature,
      emailContent: prev.emailContent.replace(prev.mailBox || "", newSignature),
    }));
  };

  function newMessageDialogheader(): JSX.Element {
    if (isMobile) {
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"start"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
        </Flex>
      );
    } else
      return (
        <Flex
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          margin={"10px"}
        >
          <Flex
            direction={"row"}
            UNSAFE_className={styles.message_heading}
            gap={"10px"}
          >
            {t("new_message", { ns: "layout_components" })}{" "}
            <NewMessageIcon size={18} />
          </Flex>
          <Flex
            direction={"row"}
            UNSAFE_className={styles.label_message_type}
            gap={"10px"}
            alignContent={"center"}
            alignItems={"center"}
          >
            {t("message_type", { ns: "layout_components" })}
            <DropDownList
              value={newEmailFields.messageTypeValue}
              style={{ width: "200px", backgroundColor: "white" }}
              data={messageTypeOptions}
              textField="text"
              dataItemKey="id"
              onChange={(event: DropDownListChangeEvent) => {
                setNewEmailFields({
                  ...newEmailFields,
                  messageType: event.value.type,
                  messageTypeValue: event.value,
                  cc: [],
                  to: [],
                });
              }}
            />
          </Flex>
        </Flex>
      );
  }

  function minimizedDialogheader(): JSX.Element {
    return (
      <Flex
        direction={"row"}
        justifyContent={"space-between"}
        alignItems={"center"}
      >
        <Flex
          direction={"row"}
          UNSAFE_className={styles.message_heading_small}
          gap={"10px"}
        >
          {newEmailFields.subject
            ? truncateText(newEmailFields.subject, 15)
            : t("new_message", { ns: "layout_components" })}{" "}
          <NewMessageIcon size={18} />
        </Flex>
      </Flex>
    );
  }

  function newMessageDialogContent(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"} marginTop={300}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            direction={"column"}
            UNSAFE_className={styles.message_modal}
            UNSAFE_style={{
              height: "calc(100dvh - 120px)",
            }}
          >
            <Flex
              direction={"column"}
              width={"100%"}
              height={"100%"}
              gap={"8px"}
              flex={1}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.message_options}
              UNSAFE_style={{
                display: "grid",
                gridTemplateRows: "auto auto auto auto auto auto auto 1fr", // Form fields + editor (1fr)
              }}
            >
              {/* Message Type Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "1" }}
                width={"calc(100vw - 32px)"}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"20%"}
                >
                  {t("message_type", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"80%"}
                >
                  <DropDownList
                    value={newEmailFields.messageTypeValue}
                    style={{ width: "100%", backgroundColor: "white" }}
                    data={messageTypeOptions}
                    textField="text"
                    dataItemKey="id"
                    onChange={(event: DropDownListChangeEvent) => {
                      setNewEmailFields({
                        ...newEmailFields,
                        messageType: event.value.type,
                        messageTypeValue: event.value,
                        cc: [],
                        to: [],
                      });
                    }}
                  />
                </View>
              </Flex>

              {/* Context Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "2" }}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"15%"}
                >
                  {t("context", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"85%"}
                >
                  <Text>{selectedItemId}</Text>
                </View>
              </Flex>

              {/* Subject Field */}
              <Flex
                direction={"row"}
                alignSelf={"stretch"}
                position={"relative"}
                UNSAFE_style={{ gridRow: "3" }}
              >
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"15%"}
                >
                  {t("subject", { ns: "layout_components" })} :
                </View>
                <View
                  UNSAFE_className={styles.label_wrapper_mobile}
                  width={"85%"}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      backgroundColor: "transparent !important",
                      padding: "5px",
                      color: "black",
                    }}
                  />
                </View>
              </Flex>

              {/* Conditional Fields Container */}
              <Flex direction={"column"} UNSAFE_style={{ gridRow: "4" }}>
                {String(newEmailFields.messageType) === "7" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"40px"}
                      marginBottom={"8px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"15%"}
                      >
                        {t("to", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"85%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="email"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>

                    {/* CC and Notify Toggle Label */}
                    {!showCcNotify && (
                      <Flex
                        direction={"row"}
                        alignSelf={"stretch"}
                        position={"relative"}
                      >
                        <div
                          onClick={() => setShowCcNotify(true)}
                          style={{ width: "100%" }}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"100%"}
                          >
                            {t("cc", { ns: "layout_components" })},{" "}
                            {t("notify", { ns: "layout_components" })}
                          </View>
                        </div>
                      </Flex>
                    )}

                    {/* CC and Notify Fields (Collapsible) */}
                    {showCcNotify && (
                      <>
                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"40px"}
                          marginBottom={"8px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"15%"}
                          >
                            {t("cc", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"85%"}
                          >
                            <ContactsContactMultipleTag
                              createParams={createParams}
                              fieldName={"cc"}
                              specificType="email"
                              setContactsSelected={newEmailFields.cc}
                            />
                          </View>
                        </Flex>

                        <Flex
                          direction={"row"}
                          alignSelf={"stretch"}
                          position={"relative"}
                          height={"40px"}
                          marginBottom={"8px"}
                        >
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"15%"}
                          >
                            {t("notify", { ns: "layout_components" })} :
                          </View>
                          <View
                            UNSAFE_className={styles.label_wrapper_mobile}
                            width={"85%"}
                          >
                            <View marginTop={"5px"}>
                              <ContactsFilterMultipleTag
                                createParams={createParams}
                                setContactsSelected={newEmailFields.notify}
                                fieldName={"notify"}
                                params={{
                                  onlyIds: false,
                                  showOnlyUc: false,
                                  onlyUser: 1,
                                  conjuctionDistrGroups: 0,
                                  ORDER: "",
                                }}
                              />
                            </View>
                          </View>
                        </Flex>
                      </>
                    )}
                  </>
                )}

                {String(newEmailFields.messageType) === "12" && (
                  <Flex
                    direction={"row"}
                    alignSelf={"stretch"}
                    position={"relative"}
                    height={"40px"}
                  >
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"15%"}
                    >
                      {t("meeting", { ns: "layout_components" })} :
                    </View>
                    <View
                      UNSAFE_className={styles.label_wrapper_mobile}
                      width={"85%"}
                    >
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        fieldName={"participants"}
                        setContactsSelected={newEmailFields.participants}
                        params={{
                          onlyIds: false,
                          showOnlyUc: false,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </Flex>
                )}

                {String(newEmailFields.messageType) === "6" && (
                  <>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                      height={"40px"}
                      marginBottom={"8px"}
                    >
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"15%"}
                      >
                        {t("phone", { ns: "layout_components" })} :
                      </View>
                      <View
                        UNSAFE_className={styles.label_wrapper_mobile}
                        width={"85%"}
                      >
                        <ContactsContactMultipleTag
                          createParams={createParams}
                          fieldName={"to"}
                          specificType="phone"
                          setContactsSelected={newEmailFields.to}
                        />
                      </View>
                    </Flex>
                    <Flex
                      direction={"row"}
                      alignSelf={"stretch"}
                      position={"relative"}
                    >
                      <Flex width={"100%"}>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="131072"
                            checked={newEmailFields.phoneStatus === "131072"}
                            label={t("incoming", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="262144"
                            checked={newEmailFields.phoneStatus === "262144"}
                            label={t("outgoing", { ns: "layout_components" })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                        <View
                          width={"33%"}
                          UNSAFE_className={styles.label_wrapper_mobile}
                        >
                          <RadioButton
                            name="phoneStatus"
                            value="524288"
                            checked={newEmailFields.phoneStatus === "524288"}
                            label={t("unsuccessful", {
                              ns: "layout_components",
                            })}
                            onChange={(event) => {
                              setNewEmailFields({
                                ...newEmailFields,
                                phoneStatus: event.value,
                              });
                            }}
                          />
                        </View>
                      </Flex>
                    </Flex>
                  </>
                )}
              </Flex>

              {/* Notify Field (for non-email message types) */}
              {String(newEmailFields.messageType) !== "7" && (
                <Flex
                  direction={"row"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  UNSAFE_style={{ gridRow: "5" }}
                >
                  <View
                    UNSAFE_className={styles.label_wrapper_mobile}
                    width={"15%"}
                  >
                    {t("notify", { ns: "layout_components" })} :
                  </View>
                  <View
                    UNSAFE_className={styles.label_wrapper_mobile}
                    width={"85%"}
                  >
                    <View marginTop={"5px"}>
                      <ContactsFilterMultipleTag
                        createParams={createParams}
                        setContactsSelected={newEmailFields.notify}
                        fieldName={"notify"}
                        params={{
                          onlyIds: false,
                          showOnlyUc: false,
                          onlyUser: 1,
                          conjuctionDistrGroups: 0,
                          ORDER: "",
                        }}
                      />
                    </View>
                  </View>
                </Flex>
              )}

              {/* From Field (Conditional) */}
              {fromEmailAddress.length > 2 && (
                <Flex
                  direction={"row"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  UNSAFE_style={{ gridRow: "6" }}
                >
                  <View
                    UNSAFE_className={styles.label_wrapper_mobile}
                    width={"15%"}
                  >
                    {t("from", { ns: "layout_components" })} :
                  </View>
                  <View
                    UNSAFE_className={styles.label_wrapper_mobile}
                    width={"85%"}
                  >
                    <View marginTop={"5px"}>
                      <DropDownList
                        value={newEmailFields.fromValue}
                        style={{
                          width: "100%",
                          backgroundColor: "white",
                          zIndex: 0,
                          border: "none",
                          marginLeft: "4px",
                        }}
                        data={fromEmailAddress}
                        textField="name"
                        dataItemKey="id"
                        onChange={(event: DropDownListChangeEvent) => {
                          handleMailboxChange(event);
                        }}
                      />
                    </View>
                  </View>
                </Flex>
              )}

              {/* File List (Conditional) */}
              {fileList.length > 0 && (
                <Flex
                  direction={"column"}
                  position={"relative"}
                  width={"100%"}
                  UNSAFE_style={{ gridRow: "7" }}
                >
                  <Flex
                    wrap
                    direction="row"
                    gap="size-100"
                    alignItems="center"
                    width="100%"
                    UNSAFE_style={{
                      backgroundColor: "#f5f7f8",
                      borderRadius: "8px",
                      padding: "8px",
                    }}
                  >
                    {fileList.map((item: any, index: number) => (
                      <View
                        key={item.id}
                        width={"30%"}
                        position={"relative"}
                        height={"20px"}
                        UNSAFE_className={styles.icon_cancel_parent}
                        id={`${item.id}_${index}`}
                      >
                        <View
                          position={"relative"}
                          width={"24px"}
                          height={"24px"}
                          UNSAFE_className={styles.icon_cancel}
                        >
                          <div onClick={() => deleteFile(item.id)}>
                            <CancelIcon size={18} />
                          </div>
                        </View>
                        <View
                          position={"relative"}
                          left={"5px"}
                          width={"70%"}
                          UNSAFE_className={styles.label_attachment}
                        >
                          {truncateText(item.fileName, 10)}
                        </View>
                        <View
                          position={"absolute"}
                          left={"8px"}
                          width={"28px"}
                          height={"28px"}
                          UNSAFE_className={styles.attachment_icon}
                        >
                          <AttachmentIcon size={18} />
                        </View>
                      </View>
                    ))}
                  </Flex>
                </Flex>
              )}

              {/* TinyMCE Editor - Takes remaining space */}
              <Flex
                direction={"column"}
                position={"relative"}
                width={"100%"}
                height={"100%"}
                alignItems={"start"}
                justifyContent={"start"}
                zIndex={0}
                flex={1}
                minHeight={"200px"}
                gridRow={"8"}
              >
                <TinyMCEEditor
                  key={"tiny_editor_mobile"}
                  initialValue={newEmailFields.emailContent}
                  onChange={(e: any) => {
                    setNewEmailFields({ ...newEmailFields, emailContent: e });
                  }}
                  height={"100%"}
                />
              </Flex>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"} marginTop={300}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else {
      return (
        <div
          {...getRootProps()}
          className={`${styles.message_modal} ${
            isDragActive ? styles.drag_active : ""
          }`}
          style={{ height: "565px", position: "relative" }}
          onDragOver={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <input {...getInputProps()} />

          {/* Overlay that appears only when dragging */}
          {isDragActive && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                zIndex: 999,
                backgroundColor: "rgba(38, 128, 235, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
              }}
            >
              <Text UNSAFE_style={{ fontWeight: "bold", fontSize: "18px" }}>
                {t("drop_files_here", { ns: "layout_components" })}
              </Text>
            </div>
          )}
          <View UNSAFE_className={styles.message_modal}>
            <Flex
              direction={"column"}
              width={"100%"}
              alignItems={"start"}
              justifyContent={"start"}
              UNSAFE_className={styles.message_options}
            >
              <Flex
                direction={"row"}
                top={"6px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
              >
                <View position={"relative"} width={"5%"} left={"8px"}>
                  {t("context", { ns: "layout_components" })}:
                </View>
                <View
                  width={"90%"}
                  marginStart={"17px"}
                  UNSAFE_className={styles.label_wrapper}
                >
                  <Text>{selectedItemId}</Text>
                </View>
              </Flex>
              <Flex
                direction={"row"}
                top={"14px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
              >
                <View
                  position={"relative"}
                  width={"5%"}
                  left={"8px"}
                  alignSelf={"center"}
                >
                  {t("subject", { ns: "layout_components" })}
                </View>
                <View
                  width={"90%"}
                  marginStart={"17px"}
                  UNSAFE_className={styles.label_wrapper}
                >
                  <TextField
                    id="txtSubject"
                    ref={txtSubjectRef}
                    defaultValue={newEmailFields.subject}
                    value={newEmailFields.subject}
                    width={"100%"}
                    height={"25px"}
                    onChange={(e) =>
                      setNewEmailFields({ ...newEmailFields, subject: e })
                    }
                    UNSAFE_style={{
                      border: "none !important",
                      boxShadow: "none",
                      backgroundColor: "transparent !important",
                      padding: "5px 0px",
                      color: "black",
                    }}
                    UNSAFE_className="borderless_textfield"
                  />
                </View>
              </Flex>
              {String(newEmailFields.messageType) === "7" && (
                <Flex
                  direction={"row"}
                  top={"22px"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  flexShrink={0}
                  gap={"size-200"}
                >
                  <View
                    position={"relative"}
                    width={"5%"}
                    left={"8px"}
                    top={"5px"}
                  >
                    {t("to", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"45%"}
                    UNSAFE_style={{ overflow: "auto" }}
                    UNSAFE_className={styles.label_wrapper}
                    marginStart={"7px"}
                  >
                    <ContactsContactMultipleTag
                      createParams={createParams}
                      fieldName={"to"}
                      specificType="email"
                      setContactsSelected={newEmailFields.to}
                    />
                  </View>
                  <View position={"relative"} width={"5%"} top={"5px"}>
                    {t("cc", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"45%"}
                    UNSAFE_style={{ overflow: "auto" }}
                    UNSAFE_className={styles.label_wrapper}
                  >
                    <ContactsContactMultipleTag
                      createParams={createParams}
                      fieldName={"cc"}
                      specificType="email"
                      setContactsSelected={newEmailFields.cc}
                    />
                  </View>
                </Flex>
              )}
              {String(newEmailFields.messageType) === "12" && (
                <Flex
                  direction={"row"}
                  top={"28px"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  flexShrink={0}
                  gap={"size-200"}
                >
                  <View
                    position={"relative"}
                    width={"8%"}
                    left={"8px"}
                    top={"5px"}
                  >
                    {t("meeting", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"92%"}
                    UNSAFE_style={{ overflow: "auto" }}
                    UNSAFE_className={styles.label_wrapper}
                  >
                    <ContactsFilterMultipleTag
                      createParams={createParams}
                      fieldName={"participants"}
                      setContactsSelected={newEmailFields.participants}
                      params={{
                        onlyIds: false,
                        //organizationId: 130946101,
                        showOnlyUc: false,
                        conjuctionDistrGroups: 0,
                        ORDER: "",
                      }}
                    />
                  </View>
                </Flex>
              )}

              {String(newEmailFields.messageType) === "6" && (
                <Flex
                  direction={"row"}
                  top={"28px"}
                  alignSelf={"stretch"}
                  position={"relative"}
                  flexShrink={0}
                  gap={"size-200"}
                >
                  <View
                    position={"relative"}
                    width={"6%"}
                    left={"8px"}
                    top={"5px"}
                  >
                    {t("phone", { ns: "layout_components" })}:
                  </View>
                  <View
                    width={"50%"}
                    UNSAFE_style={{ overflow: "auto" }}
                    UNSAFE_className={styles.label_wrapper}
                  >
                    <ContactsContactMultipleTag
                      createParams={createParams}
                      fieldName={"to"}
                      specificType="phone"
                      setContactsSelected={newEmailFields.to}
                    />
                  </View>

                  <Flex
                    direction={"row"}
                    gap={"20px"}
                    width={"44%"}
                    UNSAFE_style={{ overflow: "auto" }}
                    UNSAFE_className={styles.label_wrapper}
                  >
                    <RadioButton
                      name="phoneStatus"
                      value="131072"
                      checked={newEmailFields.phoneStatus === "131072"}
                      label={t("incoming", { ns: "layout_components" })}
                      onChange={(event) => {
                        setNewEmailFields({
                          ...newEmailFields,
                          phoneStatus: event.value,
                        });
                      }}
                    />

                    <RadioButton
                      name="phoneStatus"
                      value="262144"
                      checked={newEmailFields.phoneStatus === "262144"}
                      label={t("outgoing", { ns: "layout_components" })}
                      onChange={(event) => {
                        setNewEmailFields({
                          ...newEmailFields,
                          phoneStatus: event.value,
                        });
                      }}
                    />

                    <RadioButton
                      name="phoneStatus"
                      value="524288"
                      checked={newEmailFields.phoneStatus === "524288"}
                      label={t("unsuccessful", { ns: "layout_components" })}
                      onChange={(event) => {
                        setNewEmailFields({
                          ...newEmailFields,
                          phoneStatus: event.value,
                        });
                      }}
                    />
                  </Flex>
                </Flex>
              )}
              <Flex
                direction={"row"}
                top={"35px"}
                alignSelf={"stretch"}
                position={"relative"}
                flexShrink={0}
                gap={"size-200"}
              >
                <View
                  position={"relative"}
                  width={"6%"}
                  left={"8px"}
                  top={"5px"}
                >
                  {t("notify", { ns: "layout_components" })}:
                </View>
                <View
                  width={"44%"}
                  UNSAFE_style={{ overflow: "auto" }}
                  UNSAFE_className={styles.label_wrapper}
                >
                  <ContactsFilterMultipleTag
                    createParams={createParams}
                    setContactsSelected={newEmailFields.notify}
                    fieldName={"notify"}
                    params={{
                      onlyIds: false,
                      //organizationId: 130946101,
                      showOnlyUc: false,
                      onlyUser: 1,
                      conjuctionDistrGroups: 0,
                      ORDER: "",
                    }}
                  />
                </View>

                {fromEmailAddress.length > 2 && (
                  <>
                    <View position={"relative"} width={"5%"} top={"5px"}>
                      {t("from", { ns: "layout_components" })}:
                    </View>
                    <View
                      position={"relative"}
                      width={"45%"}
                      UNSAFE_className={styles.label_wrapper}
                    >
                      <DropDownList
                        value={newEmailFields.fromValue}
                        style={{
                          width: "100%",
                          backgroundColor: "white",
                          zIndex: 0,
                          border: "none",
                          marginLeft: "4px",
                        }}
                        data={fromEmailAddress}
                        textField="name"
                        dataItemKey="id"
                        onChange={(event: DropDownListChangeEvent) => {
                          handleMailboxChange(event);
                        }}
                      />
                    </View>
                  </>
                )}
              </Flex>
            </Flex>
            <Flex direction={"column"} position={"relative"} marginTop={"35px"}>
              {fileList.length > 0 && (
                <Flex
                  wrap
                  direction="row"
                  gap="size-100"
                  alignItems="center"
                  width="90%"
                  marginStart="50px"
                  UNSAFE_style={{
                    backgroundColor: "#f5f7f8",
                    padding: "10px",
                    borderRadius: "8px",
                  }}
                >
                  {fileList.map((item: any, index: number) => (
                    <View
                      key={item.id}
                      width={"48%"}
                      position={"relative"}
                      height={"40px"}
                      UNSAFE_className={styles.icon_cancel_parent}
                      id={`${item.id}_${index}`}
                    >
                      <View
                        position={"relative"}
                        top={"8px"}
                        width={"24px"}
                        height={"24px"}
                        UNSAFE_className={styles.icon_cancel}
                      >
                        <div onClick={() => deleteFile(item.id)}>
                          <CancelIcon size={18} />
                        </div>
                      </View>
                      <View
                        position={"relative"}
                        top={"10px"}
                        left={"35px"}
                        width={"70%"}
                        UNSAFE_className={styles.label_attachment}
                      >
                        {truncateText(item.fileName, 20)}
                      </View>
                      <View
                        position={"absolute"}
                        top={"6px"}
                        left={"8px"}
                        width={"28px"}
                        height={"28px"}
                        UNSAFE_className={styles.attachment_icon}
                      >
                        <AttachmentIcon size={18} />
                      </View>
                    </View>
                  ))}
                </Flex>
              )}
            </Flex>

            <Flex
              direction={"column"}
              position={"relative"}
              marginTop={"10px"}
              width={"100%"}
              height={"400px"}
              alignItems={"start"}
              justifyContent={"start"}
              zIndex={0}
            >
              <TinyMCEEditor
                initialValue={newEmailFields.emailContent}
                onChange={(e: any) => {
                  setNewEmailFields({ ...newEmailFields, emailContent: e });
                }}
                height={400}
              />
            </Flex>
          </View>
        </div>
      );
    }
  }

  function newMessageDialogFooter(): any {
    if (isMobile) {
      if (showLoader) {
        return (
          <Flex width="100%" justifyContent={"center"}>
            <Loader size="medium" type={"pulsing"} />
          </Flex>
        );
      } else
        return (
          <Flex
            gap={"10px"}
            direction={"column"}
            alignItems={"center"}
            UNSAFE_className={styles.email_action_bar}
          >
            <Flex
              gap={"8px"}
              alignItems={"center"}
              UNSAFE_style={{ display: "none" }}
            >
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px" }}>
                  <TemplateIcon size={18} />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("use_template", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <span style={{ marginLeft: "10px", color: "#1976d2" }}>
                  <i className="bi bi-gear" />
                </span>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("advanced", { ns: "layout_components" })}
                </Text>
              </ActionButton>
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "10px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                  <Text UNSAFE_className={styles.btn_attachment}>
                    {t("add_attachment", { ns: "layout_components" })}
                  </Text>
                </ActionButton>
              </FileTrigger>
            </Flex>
            <Flex gap={"8px"} alignItems={"center"} margin={"5px"}>
              {/* <ActionButton onPress={e => saveDraft()} UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}>
                <Text UNSAFE_className={styles.btn_attachment}>{t('save_draft_and_close', { ns: 'layout_components' })}</Text>
              </ActionButton> */}
              <FileTrigger
                allowsMultiple
                onSelect={async (e: any) => {
                  handleFilesAsync(e);
                }}
              >
                <ActionButton UNSAFE_className={styles.icon_action_button}>
                  <Content UNSAFE_style={{ marginLeft: "10px" }}>
                    <AttachmentIcon size={18} />
                  </Content>
                </ActionButton>
              </FileTrigger>
              <ContextMenuComponent
                target=".context-target"
                id="contextmenu"
                ref={contextMenuRef}
                items={menuItems}
                select={handleMenuItemClick}
              />
              <span
                className={`${styles.action_button} ${styles.draft_button} context-target`}
                style={{ width: "50px", justifyContent: "center" }}
                role="button"
                tabIndex={0}
              >
                <MoreIcon size={18} />
              </span>

              <ActionButton
                width={"250px"}
                onPress={() => handleSendEmail()}
                UNSAFE_className={`${styles.action_button_email} ${styles.send_button}`}
              >
                <Text UNSAFE_className={styles.btn_send}>
                  {t("send", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </Flex>
          </Flex>
        );
    }
    if (showLoader) {
      return (
        <Flex width="100%" justifyContent={"center"}>
          <Loader size="medium" type={"pulsing"} />
        </Flex>
      );
    } else
      return (
        <Flex
          alignItems={"center"}
          justifyContent={"space-between"}
          gap={"10px"}
          UNSAFE_className={styles.email_action_bar}
        >
          <Flex gap={"8px"} alignItems={"center"}>
            <ActionButton UNSAFE_className={styles.icon_action_button}>
              <span style={{ marginLeft: "25px" }}>
                <TemplateIcon size={18} />
              </span>
              <Text UNSAFE_className={styles.btn_attachment}>
                {t("use_template", { ns: "layout_components" })}
              </Text>
            </ActionButton>
            <ActionButton UNSAFE_className={styles.icon_action_button}>
              <span style={{ marginLeft: "25px", color: "#1976d2" }}>
                <i className="bi bi-gear" />
              </span>
              <Text UNSAFE_className={styles.btn_attachment}>
                {t("advanced", { ns: "layout_components" })}
              </Text>
            </ActionButton>
            <FileTrigger
              allowsMultiple
              onSelect={async (e: any) => {
                handleFilesAsync(e);
              }}
            >
              <ActionButton UNSAFE_className={styles.icon_action_button}>
                <Content UNSAFE_style={{ marginLeft: "25px" }}>
                  <AttachmentIcon size={18} />
                </Content>
                <Text UNSAFE_className={styles.btn_attachment}>
                  {t("add_attachment", { ns: "layout_components" })}
                </Text>
              </ActionButton>
            </FileTrigger>
          </Flex>
          <Flex gap={"8px"} alignItems={"center"}>
            {/* <button className="action-button draft-button">Save as draft and close</button> */}
            <ActionButton
              onPress={() => handleSaveDraft()}
              UNSAFE_className={`${styles.action_button} ${styles.draft_button}`}
            >
              <Text UNSAFE_className={styles.btn_attachment}>
                {t("save_draft_and_close", { ns: "layout_components" })}
              </Text>
            </ActionButton>
            {/* <button className="action-button send-button">Send</button> */}
            <ActionButton
              onPress={() => handleSendEmail()}
              UNSAFE_className={`${styles.action_button} ${styles.send_button}`}
            >
              <Text UNSAFE_className={styles.btn_send}>
                {t("send", { ns: "layout_components" })}
              </Text>
            </ActionButton>
          </Flex>
        </Flex>
      );
  }

  return (
    <div onKeyUp={handleClose}>
      <WindowComponent
        id={`new-message-window_${windowIndex}`}
        windowIndex={windowIndex}
        visible={isOpen}
        title={newMessageDialogheader()}
        minimizedTitle={minimizedDialogheader()}
        content={newMessageDialogContent()}
        footer={newMessageDialogFooter()}
        width={isMobile ? window.innerWidth : 750}
        height={isMobile ? window.innerHeight : 700}
        draggable={!isMobile}
        resizable={!isMobile}
        zIndex={1000}
        onClose={() => closeDialog(false)}
      />
    </div>
  );
}

export default NewMessageDialog;
